/* Package imports */
import React, { Suspense } from "react";
import { useRoutes } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";

/* Utility imports */
import { GlobalValues } from "../constants/global";

/* Import layouts/containers */
import DefaultLayout from "../layouts/Default";
import VerticalLayout from "../layouts/Vertical";

/* Import root component */
import Root from "./Root";
import { useSelector } from "react-redux";

/* Lazy load all views */
// Error views
const PageNotFound = React.lazy(() => import("../pages/PageNotFound"));
// Auth views
const Login = React.lazy(() => import("../pages/account/Login"));
const Logout = React.lazy(() => import("../pages/account/Logout"));
// Dashboard
const Dashboard = React.lazy(() => import("../pages/dashboard/index"));
// Visitor

const ViewTermsAndConditionsManage = React.lazy(() => import("../pages/ViewTermsAndConditionsManage"));

/* Super Admin */
const ManageOrganization = React.lazy(() => import("../pages/superAdmin/organization/ManageOrganization"));
const ListOrganization = React.lazy(() => import("../pages/superAdmin/organization/ListOrganization"));

const ListOrganizationUser = React.lazy(() => import("../pages/superAdmin/user/ListOrganizationUser"));
const ManageOrganizationUser = React.lazy(() => import("../pages/superAdmin/user/ManageOrganizationUser"));

const ListServiceCategory = React.lazy(() => import("../pages/superAdmin/serviceCategory/ListServiceCategory"));
const ManageServiceCategory = React.lazy(() => import("../pages/superAdmin/serviceCategory/ManageServiceCategory"));

const ListServiceSubCategory = React.lazy(() => import("../pages/superAdmin/serviceSubCategory/ListServiceSubCategory"));
const ManageServiceSubCategory = React.lazy(() => import("../pages/superAdmin/serviceSubCategory/ManageServiceSubCategory"));

const ListService = React.lazy(() => import("../pages/superAdmin/service/ListService"));
const ManageService = React.lazy(() => import("../pages/superAdmin/service/ManageService"));
const ReorderService = React.lazy(() => import("../pages/superAdmin/service/ReorderService"));
const ManageAgreement = React.lazy(() => import("../pages/superAdmin/agreement/ManageAgreement"));

const ListZone = React.lazy(() => import("../pages/superAdmin/zone/ListZone"));
const ManageZone = React.lazy(() => import("../pages/superAdmin/zone/ManageZone"));

/* Organization Admin */
const ListUser = React.lazy(() => import("../pages/organizationAdmin/user/ListUser"));
const ManageUser = React.lazy(() => import("../pages/organizationAdmin/user/ManageUser"));

const ListServiceRequest = React.lazy(() => import("../pages/organizationAdmin/serviceRequest/ListServiceRequest"));
const ManageServiceRequest = React.lazy(() => import("../pages/organizationAdmin/serviceRequest/ManageServiceRequest"));

/* Define loading component (fallback to suspenseful loading) */
const loading = () => <div className=""></div>;

/* Utility method for loading component */
const LoadComponent = ({ component: Component }) => {
    return (
        <Suspense fallback={loading()}>
            <Component />
        </Suspense>
    );
};

/* Declare all app routes */
const AllRoutes = () => {
    let Layout = VerticalLayout;
    const auth = useSelector((state) => {
        return state.auth;
    });

    return useRoutes([
        /* Root component which navigates to the required URL */
        { path: "/", element: <Root /> },
        {
            /* Public route */
            path: "/",
            element: <DefaultLayout />,
            children: [
                {
                    path: "account",
                    children: [
                        { path: "login", element: <LoadComponent component={Login} /> },
                        { path: "logout", element: <LoadComponent component={Logout} /> }
                    ]
                },
                {
                    path: "terms-and-conditions",
                    element: <LoadComponent component={ViewTermsAndConditionsManage} />
                }
            ]
        },
        {
            /* Protected route */
            path: "/",
            element: <PrivateRoute component={Layout} />,
            children: [
                {
                    path: "dashboard",
                    children: [
                        {
                            path: "home",
                            element: <LoadComponent component={Dashboard} />
                        }
                    ]
                }
            ]
        },
        {
            /* Protected route */
            path: "/",
            element: <PrivateRoute roles={[GlobalValues.superAdministratorRole.id]} component={Layout} />,
            children: [
                {
                    path: "organization",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListOrganization} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageOrganization} />
                        }
                    ]
                },
                {
                    path: "organization/user",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListOrganizationUser} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageOrganizationUser} />
                        }
                    ]
                },
                {
                    path: "servicecategory",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListServiceCategory} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageServiceCategory} />
                        }
                    ]
                },
                {
                    path: "servicesubcategory",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListServiceSubCategory} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageServiceSubCategory} />
                        }
                    ]
                },
                {
                    path: "service",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListService} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageService} />
                        },
                        {
                            path: "reorder",
                            element: <LoadComponent component={ReorderService} />
                        }
                    ]
                },
                {
                    path: "agreement",
                    children: [
                        {
                            path: "page",
                            element: <LoadComponent component={ManageAgreement} />
                        }
                    ]
                },
                {
                    path: "zone",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListZone} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageZone} />
                        }
                    ]
                }
            ]
        },
        {
            /* Protected route */
            path: "/",
            element: <PrivateRoute roles={[GlobalValues.organizationAdministratorRole.id]} component={Layout} />,
            children: [
                {
                    path: "user",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListUser} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageUser} />
                        }
                    ]
                },
                {
                    path: "service/service-request/",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListServiceRequest} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageServiceRequest} />
                        }
                    ]
                }
            ]
        },
        /* Wildcard for page not found */
        { path: "*", element: <LoadComponent component={PageNotFound} /> }
    ]);
};

export { AllRoutes };
